import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CheckCircle, Circle, Clock, MessageCircle, Send, Upload, FileText, Tag } from 'lucide-react';
import UserTaggingInput from './UserTaggingInput';
import FileUpload from './FileUpload';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';


const API = "/api";

const StageProgressModal = ({ isOpen, onClose, stage, product, onUpdate, onTimelineRefresh }) => {
  // Add validation to prevent white page issues
  if (!isOpen || !stage || !product) {
    console.warn('StageProgressModal: Missing required props', { isOpen, stage: !!stage, product: !!product });
    return null;
  }
  const [formData, setFormData] = useState({
    substage_1: 'not_started',
    substage_2: 'not_started',
    substage_3: 'not_started',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [stageTimelineNotes, setStageTimelineNotes] = useState([]);
  const { toast } = useToast();
  const { user } = useAuth();
  const noteInputRef = React.useRef(null);
  
  const fetchStageTimelineNotes = async () => {
    if (!product?.id || !stage) return;
    
    try {
      const response = await axios.get(`${API}/product-notes/${product.id}`);
      // Filter notes that are related to this stage (contain stage label in content)
      const stageNotes = response.data.filter(note => 
        note.content.includes(`[${stage.label}]`) || 
        note.stage_when_added === stage.key
      );
      setStageTimelineNotes(stageNotes);
    } catch (error) {
      console.error('Failed to fetch stage timeline notes:', error);
    }
  };

  useEffect(() => {
    if (isOpen && stage && product) {
      fetchStageTimelineNotes();
    }
  }, [isOpen, stage, product]);


  useEffect(() => {
    if (isOpen && stage && product) {
      fetchStageTimelineNotes();
    }
  }, [isOpen, stage, product]);

  useEffect(() => {
    if (stage && product && isOpen) {
      const stageData = product.stage_progress?.find(s => s.stage === stage.key);
      if (stageData) {
        setFormData({
          substage_1: stageData.substage_1 || 'not_started',
          substage_2: stageData.substage_2 || 'not_started',
          substage_3: stageData.substage_3 || 'not_started',
          notes: stageData.notes || ''
        });

      } else {
        // Reset form for new stage - always set all substages for data consistency
        setFormData({
          substage_1: 'not_started',
          substage_2: 'not_started',
          substage_3: 'not_started',
          notes: ''
        });
      }

      // Clear tagged users when opening modal
      setTaggedUsers([]);

      // Force clear the textarea using ref if it exists
      setTimeout(() => {
        if (noteInputRef.current) {
          noteInputRef.current.value = '';
        }
      }, 50);
    }
  }, [stage, product, isOpen]);




  const allSubstages = [
    { key: 'substage_1', label: 'Planning & Analysis' },
    { key: 'substage_2', label: 'Implementation' },
    { key: 'substage_3', label: 'Review & Testing' }
  ];

  // Define which substages to show for each stage
  const getActiveSubstages = (stageKey) => {
    const substageMap = {
      design_concept: ['substage_1'], // Planning & Analysis only
      design_prototype: ['substage_1', 'substage_2', 'substage_3'], // All 3 stages
      development: ['substage_1', 'substage_2', 'substage_3'], // All 3 stages
      testing_verification: ['substage_3'], // Review & Testing only
      documentation: ['substage_2'], // Implementation only
      release: ['substage_2'] // Implementation only
    };

    const activeSubstageKeys = substageMap[stageKey] || ['substage_1', 'substage_2', 'substage_3'];
    return allSubstages.filter(substage => activeSubstageKeys.includes(substage.key));
  };

  const statusOptions = [
    { value: 'not_started', label: 'Not Started', icon: Circle, color: 'text-slate-400' },
    { value: 'in_progress', label: 'In Progress', icon: Clock, color: 'text-blue-500' },
    { value: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-500' }
  ];

  const handleStatusChange = (substage, status) => {
    setFormData(prev => ({ ...prev, [substage]: status }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/products/${product.id}/stage-progress`, {
        stage: stage.key,
        ...formData
      });
      
      onUpdate();
      onClose();
      toast({
        title: "Success",
        description: "Stage progress updated successfully",
      });
    } catch (error) {
      console.error('[DEBUG] Error saving:', error);
      toast({
        title: "Error",
        description: "Failed to update stage progress",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimelineNote = async () => {
    if (!formData.notes.trim()) return;

    setIsSubmittingNote(true);
    const noteContent = formData.notes.trim(); // Store the content before clearing

    try {
      await axios.post(`${API}/product-notes`, {
        product_id: product.id,
        user_name: user?.full_name || user?.username || 'Unknown User',
        content: `[${stage.label}] ${noteContent}`,
        stage_when_added: stage.key,
        tagged_users: taggedUsers.map(u => u.id)
      });

      // Clear the form and tagged users immediately
      setFormData(prev => ({ ...prev, notes: '' }));
      setTaggedUsers([]);

      toast({
        title: "Success",
        description: taggedUsers.length > 0
          ? `Stage note added and ${taggedUsers.length} user(s) notified`
          : "Stage note added to timeline successfully",
      });

      // Refresh both the product data and stage timeline notes
      onUpdate();
      fetchStageTimelineNotes();

      // Trigger timeline refresh in parent component if available
      if (onTimelineRefresh) {
        onTimelineRefresh();
      }

      // Force a small delay to ensure state update is applied
      setTimeout(() => {
        // Double-check that the form is cleared
        setFormData(prev => ({ ...prev, notes: '' }));
        setTaggedUsers([]);
        // Force clear the textarea using ref if it exists
        if (noteInputRef.current) {
          noteInputRef.current.value = '';
        }
      }, 100);

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add note to timeline",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_name', user?.full_name || user?.username || 'Unknown User');
      formData.append('upload_source', 'stage'); // Mark as uploaded from stage
      formData.append('stage_key', stage.key); // Pass the stage key

      await axios.post(`${API}/product-notes/upload/${product.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setShowFileUpload(false);
      toast({
        title: "Success",
        description: `File uploaded from ${stage.label} stage`,
      });

      // Refresh both the product data and stage timeline notes
      onUpdate();
      fetchStageTimelineNotes();

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload file",
        variant: "destructive",
      });
    }
  };

  if (!stage) return null;

  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.value === status);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${stage.color}`} />
            <span>{stage.label}</span>
          </DialogTitle>
          <p className="text-sm text-slate-600">
            Update progress and manage documents for this stage
          </p>
        </DialogHeader>

        <Tabs defaultValue="progress" className="space-y-4">
          <TabsList className="grid w-full grid-cols-1">
            <TabsTrigger value="progress" className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4" />
              <span>Progress</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="progress" className="space-y-6">
            {/* Substages */}
            <div className="space-y-4">
              {getActiveSubstages(stage.key).map((substage) => {
                const currentStatus = formData[substage.key];
                const statusConfig = getStatusConfig(currentStatus);
                const StatusIcon = statusConfig?.icon;

                return (
                  <div key={substage.key} className="space-y-2">
                    <Label className="text-sm font-medium">
                      {substage.label}
                    </Label>
                    
                    <Select
                      value={currentStatus}
                      onValueChange={(value) => handleStatusChange(substage.key, value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          <div className="flex items-center space-x-2">
                            {StatusIcon && (
                              <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                            )}
                            <span>{statusConfig?.label}</span>
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => {
                          const Icon = option.icon;
                          return (
                            <SelectItem key={option.value} value={option.value}>
                              <div className="flex items-center space-x-2">
                                <Icon className={`w-4 h-4 ${option.color}`} />
                                <span>{option.label}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>

            {/* Progress Summary */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Stage Progress</span>
                <Badge variant="outline">
                  {(() => {
                    const activeSubstages = getActiveSubstages(stage.key);
                    const completedCount = activeSubstages.filter(substage =>
                      formData[substage.key] === 'completed'
                    ).length;
                    return `${completedCount}/${activeSubstages.length} Complete`;
                  })()}
                </Badge>
              </div>
              <div className="flex space-x-1">
                {(() => {
                  const activeSubstages = getActiveSubstages(stage.key);
                  return activeSubstages.map((substage) => {
                    const status = formData[substage.key];
                    return (
                      <div
                        key={substage.key}
                        className={`flex-1 h-2 rounded ${
                          status === 'completed' ? 'bg-green-500' :
                          status === 'in_progress' ? 'bg-blue-500' :
                          'bg-slate-300'
                        }`}
                      />
                    );
                  });
                })()}
              </div>
            </div>

            {/* Enhanced Timeline Notes */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-slate-600" />
                <Label className="text-sm font-medium">Timeline Notes</Label>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <UserTaggingInput
                  content={formData.notes}
                  onContentChange={(value) => setFormData(prev => ({ ...prev, notes: value }))}
                  taggedUsers={taggedUsers}
                  onTaggedUsersChange={setTaggedUsers}
                  placeholder={`Add a note for ${stage.label} stage... Use @username to tag users`}
                  productId={product.id}
                  inputRef={noteInputRef}
                />
                
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleAddTimelineNote}
                    disabled={!formData.notes.trim() || isSubmittingNote}
                    size="sm"
                    className="flex-1"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmittingNote ? 'Adding to Timeline...' : 'Add to Timeline'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFileUpload(!showFileUpload)}
                    className="flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    File
                  </Button>
                </div>
                
                {/* File Upload Section */}
                {showFileUpload && (
                  <div className="pt-3 border-t border-slate-200">
                    <FileUpload onFileUpload={handleFileUpload} />
                  </div>
                )}
              </div>
                
              {/* Existing Stage Timeline Notes */}
              {stageTimelineNotes.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-600" />
                    <Label className="text-sm font-medium">Recent Stage Notes</Label>
                    <Badge variant="outline" className="text-xs">
                      {stageTimelineNotes.length} note{stageTimelineNotes.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {stageTimelineNotes.slice(0, 5).map((note) => {
                      const formatDateTime = (dateString) => {
                        const date = new Date(dateString);
                        return date.toLocaleString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour12: false
                        });
                      };
                      
                      return (
                        <div key={note.id} className="bg-white border border-slate-200 rounded p-2">
                          <div className="flex items-start justify-between mb-1">
                            <div className="text-xs font-medium text-slate-900">
                              {note.user_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatDateTime(note.created_at)}
                            </div>
                          </div>
                          <div className="text-xs text-slate-600">
                            {note.content.replace(`[${stage.label}] `, '')}
                          </div>
                          {note.tagged_users && note.tagged_users.length > 0 && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                <Tag className="w-2 h-2 mr-1" />
                                {note.tagged_users.length} tagged
                              </Badge>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {stageTimelineNotes.length > 5 && (
                      <div className="text-xs text-slate-500 text-center py-2">
                        And {stageTimelineNotes.length - 5} more notes in Timeline & Notes...
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>


        </Tabs>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            {loading ? 'Updating...' : 'Update Progress'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StageProgressModal;